import { resolveApiBase } from './apiClient';

function parseFilenameFromContentDisposition(header) {
  if (!header) return null;

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  const plainMatch = header.match(/filename=([^;]+)/i);
  if (plainMatch?.[1]) {
    return plainMatch[1].trim();
  }

  return null;
}

export function saveBlobAsFile(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function fetchBessAnomalyCatalog(portfolioName) {
  const path = `/api/diagnostics/portfolio/${encodeURIComponent(portfolioName)}/batteries/reports`;
  const res = await fetch(`${resolveApiBase()}${path}`, { mode: 'cors' });
  const data = await res.json();

  if (!res.ok) {
    throw new Error(data?.error || 'Failed to load battery anomaly reports');
  }

  return data;
}

export async function fetchIsolationForestWatchlist(portfolioName, params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '') return;
    search.set(key, String(value));
  });

  const path = `/api/diagnostics/portfolio/${encodeURIComponent(portfolioName)}/batteries/isolation-forest?${search}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10 * 60 * 1000);

  try {
    const res = await fetch(`${resolveApiBase()}${path}`, {
      mode: 'cors',
      signal: controller.signal,
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data?.error || 'Failed to compute Isolation Forest watchlist');
    }
    return data;
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error('Analysis timed out. Try a shorter date range or a smaller series.');
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function downloadBessAnomalyArtifact(portfolioName, params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '') return;
    search.set(key, String(value));
  });

  const path = `/api/diagnostics/portfolio/${encodeURIComponent(portfolioName)}/batteries/reports/download?${search}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15 * 60 * 1000);

  try {
    const res = await fetch(`${resolveApiBase()}${path}`, {
      mode: 'cors',
      signal: controller.signal,
    });
    const contentType = res.headers.get('content-type') || '';

    if (!res.ok) {
      if (contentType.includes('application/json')) {
        const data = await res.json();
        throw new Error(data?.error || 'Download failed');
      }
      throw new Error((await res.text()) || 'Download failed');
    }

    const blob = await res.blob();
    const fileName =
      parseFilenameFromContentDisposition(res.headers.get('content-disposition'))
      || `${params.family}-${params.series}.${params.artifact || 'pdf'}`;

    return { blob, fileName };
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error('Report generation timed out. Try a smaller series or shorter date range.');
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function downloadAnomalyDetailPdf(portfolioName, params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '') return;
    if (Array.isArray(value)) {
      search.set(key, value.join(','));
      return;
    }
    search.set(key, String(value));
  });

  const path = `/api/diagnostics/portfolio/${encodeURIComponent(portfolioName)}/batteries/isolation-forest/pdf?${search}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15 * 60 * 1000);

  try {
    const res = await fetch(`${resolveApiBase()}${path}`, {
      mode: 'cors',
      signal: controller.signal,
    });
    const contentType = res.headers.get('content-type') || '';

    if (!res.ok) {
      if (contentType.includes('application/json')) {
        const data = await res.json();
        throw new Error(data?.error || 'PDF download failed');
      }
      throw new Error((await res.text()) || 'PDF download failed');
    }

    const blob = await res.blob();
    const fileName =
      parseFilenameFromContentDisposition(res.headers.get('content-disposition'))
      || `BESS_Anomaly_Detail_${params.series || 'ALL'}.pdf`;

    return { blob, fileName };
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error('PDF generation timed out. Try a shorter date range or fewer batteries.');
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}
